// @flow
import React from "react";
import type { Context, Node } from "react";
import { IntlProvider } from "react-intl";

import { i18n } from "@lingui/core";
import { I18nProvider } from "@lingui/react";

import { loadFeatureMatrixAsync, getSysFlag } from "../lib/BusinessLogic";
import { MSG_TYPES, subscribe, unsubscribe } from "../lib/PubSub";
import { getUserLang } from "../lib/Auth";

import messages_sl from "../translations/sl.json";
import messages_en from "../translations/en.json";
import messages_de from "../translations/de.json";
import messages_cs from "../translations/cs.json";

// lingui locales
import { messages as messages_en_new } from "../locales/en.ts";
import { messages as messages_sl_new } from "../locales/sl.ts";

// use en for unsupported languages
const messages_new = {
    en: messages_en_new,
    sl: messages_sl_new,
    de: messages_en_new,
    cs: messages_en_new,
};

const LANGUAGE_CONFIG = {
    sl: {
        messages: messages_sl,
        locale: "sl-SI",
        aliases: ["si", "sl"],
    },
    de: {
        messages: messages_de,
        locale: "de-DE",
        aliases: ["de"],
    },
    cs: {
        messages: messages_cs,
        locale: "cs-CZ",
        aliases: ["cs", "cz"],
    },
    en: {
        messages: messages_en,
        locale: "en-US",
        aliases: ["en"],
    },
};

type Language = $Keys<typeof LANGUAGE_CONFIG>;

const DEFAULT_LANGUAGE: Language = "en";

type ContextValue = {
    current_language: Language,
    locale: string,
    messages: typeof messages_en,
    switchLanguage: (lang: Language) => void,
};

const IntlContext: Context<ContextValue> = React.createContext<ContextValue>({
    current_language: "en",
    locale: "en-US",
    messages: messages_en,
    switchLanguage: () => {},
});
IntlContext.displayName = "Internationalization context";

type Props = {
    children: Node,
};

type State = {
    current_language: Language,
    locale: string,
    messages: typeof messages_en,
};

/**
 * Check if a string is a valid Language
 */
const isLanguageSupported = (lang: string): boolean => {
    return Object.prototype.hasOwnProperty.call(LANGUAGE_CONFIG, lang);
};

/**
 * Get browser's language without region code
 */
const getBrowserLanguage = (): string => {
    return navigator.language.split(/[-_]/)[0];
};

/**
 * Normalize and validate language code
 */
const normalizeLanguage = (lang: string): Language => {
    const normalized = lang.toLowerCase().trim();

    // Check direct match
    if (isLanguageSupported(normalized)) {
        // $FlowFixMe: Should be valid Language at this point (improve in TypeScript if needed)
        return normalized;
    }

    return DEFAULT_LANGUAGE;
};

/**
 * Determine which language to use based on priority:
 * 1. User profile language
 * 2. Browser language
 * 3. Default language of the organization
 */
const getLanguage = (): Language => {
    const user_lang = getUserLang();

    if (user_lang) {
        return normalizeLanguage(user_lang);
    }

    const browser_lang = getBrowserLanguage();
    if (browser_lang && isLanguageSupported(browser_lang)) {
        // $FlowFixMe: Should be valid Language at this point (improve in TypeScript if needed)
        return browser_lang;
    }

    const sys_lang = getSysFlag("lang");
    if (sys_lang) {
        return normalizeLanguage(sys_lang);
    }

    return DEFAULT_LANGUAGE;
};

// Module-level language ref for use outside React (translate, getLang, etc.)
let current_language_ref: Language = getLanguage();

const updateLanguageRef = (lang: Language) => {
    current_language_ref = lang;
};

/** Load and activate Lingui catalog so useLingui() works before first paint. */
const activateLinguiLocale = (lang: Language) => {
    i18n.load(lang, messages_new[lang]);
    i18n.activate(lang);
};

class IntlProviderWrapper extends React.Component<Props, State> {
    user_profile_reloaded_sub_id: string;
    logout_sub_id: string;

    constructor(props: Props) {
        super(props);

        const current_language = getLanguage();
        const config = LANGUAGE_CONFIG[current_language];

        activateLinguiLocale(current_language);
        updateLanguageRef(current_language);

        this.state = {
            current_language,
            locale: config.locale,
            messages: config.messages,
        };

        // Subscribe to language-affecting events
        this.user_profile_reloaded_sub_id = subscribe(MSG_TYPES.user_profile_reloaded, this.handleLanguageUpdate);
        this.logout_sub_id = subscribe(MSG_TYPES.logout, this.handleLanguageUpdate);
    }

    async componentDidMount() {
        try {
            await loadFeatureMatrixAsync();
            this.updateLanguage();
        } catch (error) {
            console.error("Failed to load feature matrix:", error);
        }
    }

    componentWillUnmount() {
        // Clean up subscriptions
        unsubscribe(this.user_profile_reloaded_sub_id);
        unsubscribe(this.logout_sub_id);
    }

    handleLanguageUpdate = () => {
        this.updateLanguage();
    };

    /**
     * Update language based on current user/system settings
     */
    updateLanguage = () => {
        const lang = getLanguage();
        console.log("Setting language based on user/server setting:", lang);
        this.switchLanguage(lang);
    };

    /**
     * Switch to a specific language
     */
    switchLanguage = (lang: Language) => {
        console.log(`Switching language to: ${lang}`);

        const config = LANGUAGE_CONFIG[lang];
        activateLinguiLocale(lang);
        updateLanguageRef(lang);

        this.setState({
            current_language: lang,
            locale: config.locale,
            messages: config.messages,
        });
    };

    getContextValue = (): ContextValue => {
        return {
            current_language: this.state.current_language,
            locale: this.state.locale,
            messages: this.state.messages,
            switchLanguage: this.switchLanguage,
        };
    };

    render() {
        const { children } = this.props;
        const { locale, messages } = this.state;

        return (
            <I18nProvider i18n={i18n}>
                <IntlContext.Provider value={this.getContextValue()}>
                    <IntlProvider
                        key={locale}
                        locale={locale}
                        messages={messages}
                        defaultLocale="en-US"
                        onError={error => {
                            console.error("IntlProvider error:", error);
                        }}
                    >
                        {children}
                    </IntlProvider>
                </IntlContext.Provider>
            </I18nProvider>
        );
    }
}

/**
 * Translate a message code to current language
 * For use outside React components
 */
const translate = (code: string, default_msg?: string): string => {
    const { messages } = LANGUAGE_CONFIG[current_language_ref];
    return messages[code] || default_msg || `<MISSING: ${code}>`;
};

/**
 * Get current language code
 */
const getLang = (): Language => {
    return current_language_ref;
};

/**
 * Get current locale string (e.g., 'en-US')
 */
const getLocale = (): string => {
    return LANGUAGE_CONFIG[current_language_ref].locale;
};

/**
 * Check if a translation exists for a given code
 */
const hasTranslation = (code: string): boolean => {
    const { messages } = LANGUAGE_CONFIG[current_language_ref];
    return messages[code] !== undefined;
};

export { DEFAULT_LANGUAGE, IntlProviderWrapper, IntlContext, translate, getLang, getLocale, hasTranslation };
export type { Language, ContextValue };
